import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/common/header";
import BackToTop from "../components/common/backToTop";
import {
  Activity,
  BarChart3,
  Search,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

const MARKET_URL =
  "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=80&page=1&sparkline=false&price_change_percentage=24h";

const FALLBACK_MARKETS = [
  { id: "bitcoin", name: "Bitcoin", symbol: "btc", current_price: 111000, market_cap: 2200000000000, total_volume: 32000000000, price_change_percentage_24h: 0.8 },
  { id: "ethereum", name: "Ethereum", symbol: "eth", current_price: 4300, market_cap: 520000000000, total_volume: 21000000000, price_change_percentage_24h: -0.4 },
  { id: "polygon", name: "Polygon", symbol: "pol", current_price: 0.27, market_cap: 2800000000, total_volume: 140000000, price_change_percentage_24h: 1.2 },
  { id: "arbitrum", name: "Arbitrum", symbol: "arb", current_price: 0.46, market_cap: 2400000000, total_volume: 180000000, price_change_percentage_24h: -1.1 },
  { id: "optimism", name: "Optimism", symbol: "op", current_price: 0.72, market_cap: 1300000000, total_volume: 110000000, price_change_percentage_24h: 2.4 },
  { id: "binancecoin", name: "BNB", symbol: "bnb", current_price: 820, market_cap: 119000000000, total_volume: 1900000000, price_change_percentage_24h: 0.3 },
  { id: "usd-coin", name: "USDC", symbol: "usdc", current_price: 1, market_cap: 74000000000, total_volume: 9800000000, price_change_percentage_24h: 0.01 },
];

function money(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: Math.abs(number) >= 100000 ? "compact" : "standard",
    maximumFractionDigits: number >= 100 ? 0 : 4,
  }).format(number);
}

function compact(value) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

export default function DashboardPage() {
  const [markets, setMarkets] = useState(FALLBACK_MARKETS);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("fallback");
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadMarkets() {
      try {
        const response = await fetch(MARKET_URL, { cache: "no-store" });
        if (!response.ok) throw new Error("market api unavailable");
        const data = await response.json();
        if (!cancelled && Array.isArray(data) && data.length) {
          setMarkets(data);
          setStatus("live");
          setUpdatedAt(new Date());
        }
      } catch {
        if (!cancelled) {
          setStatus("fallback");
          setUpdatedAt(new Date());
        }
      }
    }

    loadMarkets();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return markets;
    return markets.filter(
      (item) =>
        item.name?.toLowerCase().includes(value) ||
        item.symbol?.toLowerCase().includes(value),
    );
  }, [markets, query]);

  const summary = useMemo(() => {
    const totalCap = markets.reduce((sum, item) => sum + Number(item.market_cap || 0), 0);
    const totalVolume = markets.reduce((sum, item) => sum + Number(item.total_volume || 0), 0);
    const gainers = markets.filter((item) => Number(item.price_change_percentage_24h || 0) > 0).length;
    const avgMove = markets.length
      ? markets.reduce((sum, item) => sum + Number(item.price_change_percentage_24h || 0), 0) / markets.length
      : 0;
    return { totalCap, totalVolume, gainers, avgMove };
  }, [markets]);

  return (
    <div className="market-tracker-page">
      <Header />
      <BackToTop />
      <main className="market-tracker-shell">
        <section className="market-tracker-hero">
          <div>
            <span className="auto-eyebrow">Market tracker</span>
            <h1>Live market context for ChainIntel signals.</h1>
            <p>
              A lightweight market view for the networks our bots watch. It
              keeps local fallback data so the product never drops to a blank
              page when an external provider is slow.
            </p>
          </div>
          <div className={`market-feed-card ${status}`}>
            <ShieldCheck size={28} />
            <span>Feed</span>
            <strong>{status === "live" ? "Live" : "Fallback"}</strong>
            <small>{updatedAt ? `updated ${updatedAt.toLocaleTimeString()}` : "warming up"}</small>
          </div>
        </section>

        <section className="market-summary-grid">
          <MarketStat icon={<BarChart3 />} label="Tracked assets" value={markets.length} />
          <MarketStat icon={<Activity />} label="Market cap" value={money(summary.totalCap)} />
          <MarketStat icon={<TrendingUp />} label="24h volume" value={money(summary.totalVolume)} />
          <MarketStat icon={summary.avgMove >= 0 ? <TrendingUp /> : <TrendingDown />} label="Avg 24h move" value={`${summary.avgMove.toFixed(2)}%`} tone={summary.avgMove >= 0 ? "good" : "bad"} />
        </section>

        <section className="market-toolbar">
          <Search size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search token or symbol"
          />
          <span>{summary.gainers} gainers</span>
        </section>

        <section className="market-table">
          <div className="market-table-head">
            <span>Asset</span>
            <span>Price</span>
            <span>24h</span>
            <span>Market Cap</span>
            <span>Volume</span>
          </div>
          {filtered.slice(0, 40).map((item) => {
            const move = Number(item.price_change_percentage_24h || 0);
            return (
              <article className="market-row" key={item.id || item.symbol}>
                <div>
                  {item.image ? <img src={item.image} alt="" /> : <span className="market-token-fallback">{item.symbol?.slice(0, 2)?.toUpperCase()}</span>}
                  <strong>{item.name}</strong>
                  <em>{item.symbol?.toUpperCase()}</em>
                </div>
                <b>{money(item.current_price)}</b>
                <b className={move >= 0 ? "good" : "bad"}>{move.toFixed(2)}%</b>
                <span>{compact(item.market_cap)}</span>
                <span>{compact(item.total_volume)}</span>
              </article>
            );
          })}
        </section>
      </main>
    </div>
  );
}

function MarketStat({ icon, label, value, tone = "" }) {
  return (
    <article className={`market-stat ${tone}`}>
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
